import { Suspense } from 'react';
import {
  ApiBlueprint,
  configApiRef,
  createExtensionInput,
  createFrontendModule,
  createRouteRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  AddonBlueprint,
  attachTechDocsAddonComponentData,
} from '@backstage/plugin-techdocs-react/alpha';
import {
  TechDocsAddonLocations,
  techdocsStorageApiRef,
} from '@backstage/plugin-techdocs-react';

import { RdapTechDocsStorageApi } from './apis/RdapTechDocsStorageApi';
import { techdocsRdapClientApiRef, TechDocsRdapClient } from './apis/techdocsRdapClient';
import { techdocsRdapVersionStoreApiRef, TechDocsRdapVersionStore } from './apis/versionStore';
import { RdapTechDocsPage } from './components/RdapTechDocsPage';
import { RdapTechDocsStyleOverrides } from './components/RdapTechDocsStyleOverrides';

const rdapTechDocsEntityContent = EntityContentBlueprint.makeWithOverrides({
  inputs: {
    addons: createExtensionInput([AddonBlueprint.dataRefs.addon]),
  },

  factory(originalFactory, context) {
    return originalFactory(
      {
        path: 'docs',
        title: 'Documentation',
        group: 'documentation',

        routeRef: createRouteRef({
          aliasFor: 'techdocs.entityContent',
        }),

        loader: () => {
          const directAddons = context.inputs.addons.map(output =>
            output.get(AddonBlueprint.dataRefs.addon),
          );

          const addons = directAddons.map(options => {
            const Addon = options.component;

            attachTechDocsAddonComponentData(Addon, options);

            return (
              <Suspense fallback={null} key={options.name}>
                <Addon />
              </Suspense>
            );
          });

          return Promise.resolve(<RdapTechDocsPage addons={addons} />);
        },
      },
      context,
    );
  },
});

const rdapTechDocsVersionStoreApi = ApiBlueprint.make({
  name: 'version-store',
  params: defineParams =>
    defineParams({
      api: techdocsRdapVersionStoreApiRef,
      deps: {},
      factory: () => new TechDocsRdapVersionStore(),
    }),
});

const rdapTechDocsClientApi = ApiBlueprint.make({
  name: 'client',
  params: defineParams =>
    defineParams({
      api: techdocsRdapClientApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new TechDocsRdapClient({ discoveryApi, fetchApi }),
    }),
});

// Overrides the stock techdocs `storage` API (api:techdocs/storage) so that
// whenever the version picker selects a non-default branch/tag, doc content
// and sync requests go through the techdocs-rdap backend instead.
const rdapTechDocsStorageApi = ApiBlueprint.make({
  name: 'storage',
  params: defineParams =>
    defineParams({
      api: techdocsStorageApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        versionStore: techdocsRdapVersionStoreApiRef,
      },
      factory: ({ configApi, discoveryApi, fetchApi, versionStore }) =>
        new RdapTechDocsStorageApi({
          configApi,
          discoveryApi,
          fetchApi,
          versionStore,
        }),
    }),
});

// Widens/enlarges the generated docs' own mkdocs tab bar, which lives in a
// shadow root this plugin's CSS can't otherwise reach.
const rdapTechDocsStyleOverridesAddon = AddonBlueprint.make({
  name: 'style-overrides',
  params: {
    name: 'RdapTechDocsStyleOverrides',
    location: TechDocsAddonLocations.Content,
    component: RdapTechDocsStyleOverrides,
  },
});

export const techdocsModuleRdap = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    rdapTechDocsEntityContent,
    rdapTechDocsVersionStoreApi,
    rdapTechDocsClientApi,
    rdapTechDocsStorageApi,
    rdapTechDocsStyleOverridesAddon,
  ],
});
